import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { PlatformApp, PlatformAppUiSchema } from '@typings';
import { toast } from '@features';
import { installPlatformApp } from '@services/platformApps.services';
import { getPlatformAppsInstances } from '@thunks/platformApps.thunks';
import { contextNamesSelector } from '@selectors';
import { useDispatch, useSelector } from '@hooks';
import {
  flattenObject,
  formatToCamelCase,
  invariant,
  isArray,
  makeAppSchema,
  path,
} from '@utils';
import { extractSchema, getSchemaDefaultValues } from '@utils/helpers';

import { Button, Field, Modal, Theme } from '@components';
import { AppConstructorNavigationProvider } from '@components/Providers';

import { PlatformAppLogo } from './PlatformAppLogo';
import { PlatformAppsUiSchemaHandler } from './PlatformAppsUiSchemaHandler';

type ResponseError = {
  message: string;
  path: string[];
};

type Schema = z.infer<ReturnType<typeof makeAppSchema>>;

type Props = {
  template: PlatformApp.Template;
};

export const PlatformAppsInstallingConstructor: React.FC<Props> = ({
  template,
  template: { input },
}) => {
  const dispatch = useDispatch();
  const { organizationName, clusterName, projectName } =
    useSelector(contextNamesSelector);

  const navigate = useNavigate();

  const { title, logo, version, name } = formatToCamelCase(
    template,
  ) as PlatformApp.Template;

  const { uiSchemas } = extractSchema(input);

  const schema = makeAppSchema(uiSchemas);
  const defaultValues = getSchemaDefaultValues(uiSchemas);

  const methods = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const [loading, setLoading] = useState(false);

  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
    setError,
  } = methods;

  const makeUiSection = (schema: PlatformAppUiSchema.Model, index: number) => (
    <PlatformAppsUiSchemaHandler key={index} schema={schema} />
  );

  const handleResponseErrors = (errors: ResponseError[]) => {
    const formValues = flattenObject(getValues());

    errors.forEach(({ message, path }) => {
      const fieldName = path.join('.');

      /**
       * If field name is registered in schema, attach error message to field.
       * Otherwise, toastify error message
       */
      if (fieldName in formValues) {
        // @ts-expect-error todo: fix type
        setError(fieldName, { message });
      } else {
        const toastMessage = fieldName ? `${fieldName}: ${message}` : message;

        toast.error(toastMessage);
      }
    });
  };

  const handleFormSubmit = handleSubmit(async ({ displayName, ...input }) => {
    try {
      setLoading(true);

      invariant(clusterName);
      invariant(projectName);

      const payload = {
        template_name: name,
        template_version: version,
        display_name: displayName,
        input,
      };

      const { id: instanceId } = await installPlatformApp({
        organizationName,
        clusterName,
        projectName,
        payload,
      });

      toast.success('App is being installed');

      await dispatch(
        getPlatformAppsInstances({
          organizationName,
          clusterName,
          projectName,
        }),
      );

      navigate(path.appsInstance(instanceId), { replace: true });
    } catch (error) {
      const { message, detail, errors } = error as {
        message?: string;
        detail?: string;
        errors?: ResponseError[];
      };

      if (isArray(errors)) {
        handleResponseErrors(errors);
      }

      toast.error(message ?? detail ?? 'Something went wrong');
    } finally {
      setLoading(false);
    }
  });

  return (
    <AppConstructorNavigationProvider>
      <FormProvider {...methods}>
        <Theme.Container className="relative mx-auto flex w-full max-w-[720px] flex-col p-0">
          <div className="flex items-center gap-4 border-b border-neural-02 px-10 py-7">
            <PlatformAppLogo src={logo} />
            <div className="flex flex-col">
              <h3 className="text-h4">{title}</h3>
              <p className="-mt-2 text-neural-04">Version: {version}</p>
            </div>
          </div>
          <form noValidate className="flex flex-1 flex-col gap-12 px-10 py-7">
            {uiSchemas.map(makeUiSection)}
            <div className="flex flex-col gap-3">
              <p className="text-h4">Metadata</p>
              <Field.Input
                {...register('displayName')}
                label="App name"
                note="App display name"
                error={errors.displayName?.message}
              />
            </div>
          </form>
          <Modal.Footer sticky className="mt-auto rounded-b-2xl px-10 py-8">
            <Button
              loading={loading}
              className="px-10 capitalize"
              onClick={handleFormSubmit}
            >
              Install
            </Button>
          </Modal.Footer>
        </Theme.Container>
      </FormProvider>
    </AppConstructorNavigationProvider>
  );
};
